import i18n from 'modules/core/lib/i18n'

import Allowance from 'modules/timeoff/store/models/Allowance'
import type { Cycle } from 'modules/timeoff/store/models/AllowanceStat'

interface IKeys {
  [key: string]: any
}

const EMPLOYEE_CYCLE_EXPLAINERS: IKeys = {
  [Allowance.CARRY_OVER_STRATEGIES.unlimited]: unlimitedCarryOverEmployeeCycleExplainer,
  [Allowance.CARRY_OVER_STRATEGIES.one_units]: oneUnitsCarryOverEmployeeCycleExplainer,
  [Allowance.CARRY_OVER_STRATEGIES.zero_units]: zeroUnitsCarryOverExplainer,
  [Allowance.CARRY_OVER_STRATEGIES.several_units]: severalUnitsCarryOverEmployeeCycleExplainer,
}

const ALLOWANCE_CYCLE_EXPLAINERS: IKeys = {
  [Allowance.CARRY_OVER_STRATEGIES.unlimited]: unlimitedCarryOverAllowanceExplainer,
  [Allowance.CARRY_OVER_STRATEGIES.one_units]: oneUnitsCarryOverAllowanceExplainer,
  [Allowance.CARRY_OVER_STRATEGIES.zero_units]: zeroUnitsCarryOverExplainer,
  [Allowance.CARRY_OVER_STRATEGIES.several_units]: severalUnitsCarryOverAllowanceExplainer,
}

function zeroUnitsCarryOverExplainer(allowance: Allowance): string {
  return allowance.isDays
    ? i18n.t('time_off.carry_over_explainer.carry_over.days.description.zero_days')
    : i18n.t('time_off.carry_over_explainer.carry_over.hours.description.zero_hours')
}

function unlimitedCarryOverAllowanceExplainer(
  allowance: Allowance,
  cycleLimits: Cycle | null | undefined
): string {
  if (allowance.isDays) {
    if (allowance.neverExpires) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.unlimited.never_expire'
      )
    }
    if (allowance.expireInMonths < 12) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.unlimited.expire_in_less_than_one_year',
        { month: expiringMonth(allowance, cycleLimits) }
      )
    }
    if (allowance.expireInYears === 1) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.unlimited.expire_in_one_year'
      )
    }
    if (allowance.expiresInRoundYears) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.unlimited.expire_in_years',
        { years: allowance.expireInYears }
      )
    }
    return i18n.t(
      'time_off.carry_over_explainer.carry_over.days.description.unlimited.expire_in_months',
      { months: allowance.expireInMonths }
    )
  }

  if (allowance.isHourly) {
    if (allowance.neverExpires) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.unlimited.never_expire'
      )
    }
    if (allowance.expireInMonths < 12) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.unlimited.expire_in_less_than_one_year',
        { month: expiringMonth(allowance, cycleLimits) }
      )
    }
    if (allowance.expireInYears === 1) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.unlimited.expire_in_one_year'
      )
    }
    if (allowance.expiresInRoundYears) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.unlimited.expire_in_years',
        { years: allowance.expireInYears }
      )
    }
    return i18n.t(
      'time_off.carry_over_explainer.carry_over.hours.description.unlimited.expire_in_months',
      { months: allowance.expireInMonths }
    )
  }

  throw new Error(`Missing translation for unlimitedCarryOverAllowanceExplainer`)
}

function oneUnitsCarryOverAllowanceExplainer(
  allowance: Allowance,
  cycleLimits: Cycle | null | undefined
): string {
  if (allowance.isDays) {
    if (allowance.neverExpires) {
      return i18n.t('time_off.carry_over_explainer.carry_over.days.description.one.never_expire')
    }
    if (allowance.expireInMonths < 12) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.one.expire_in_less_than_one_year',
        { month: expiringMonth(allowance, cycleLimits) }
      )
    }
    if (allowance.expireInYears === 1) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.one.expire_in_one_year'
      )
    }
    if (allowance.expiresInRoundYears) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.one.expire_in_years',
        { years: allowance.expireInYears }
      )
    }
    return i18n.t(
      'time_off.carry_over_explainer.carry_over.days.description.one.expire_in_months',
      { months: allowance.expireInMonths }
    )
  }

  if (allowance.isHourly) {
    if (allowance.neverExpires) {
      return i18n.t('time_off.carry_over_explainer.carry_over.hours.description.one.never_expire')
    }
    if (allowance.expireInMonths < 12) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.one.expire_in_less_than_one_year',
        { month: expiringMonth(allowance, cycleLimits) }
      )
    }
    if (allowance.expireInYears === 1) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.one.expire_in_one_year'
      )
    }
    if (allowance.expiresInRoundYears) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.one.expire_in_years',
        { years: allowance.expireInYears }
      )
    }
    return i18n.t(
      'time_off.carry_over_explainer.carry_over.hours.description.one.expire_in_months',
      { months: allowance.expireInMonths }
    )
  }

  throw new Error(`Missing translation for oneUnitsCarryOverAllowanceExplainer`)
}

function severalUnitsCarryOverAllowanceExplainer(
  allowance: Allowance,
  cycleLimits: Cycle | null | undefined
): string {
  const units = { units: allowance.carryOverUnits }

  if (allowance.isDays) {
    if (allowance.neverExpires) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.several.never_expire',
        { ...units, month: expiringMonth(allowance, cycleLimits) }
      )
    }
    if (allowance.expireInMonths < 12) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.several.expire_in_less_than_one_year',
        { ...units, month: expiringMonth(allowance, cycleLimits) }
      )
    }
    if (allowance.expireInYears === 1) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.several.expire_in_one_year',
        { ...units }
      )
    }
    if (allowance.expiresInRoundYears) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.days.description.several.expire_in_years',
        { ...units, years: allowance.expireInYears }
      )
    }
    return i18n.t(
      'time_off.carry_over_explainer.carry_over.days.description.several.expire_in_months',
      { ...units, months: allowance.expireInMonths }
    )
  }

  if (allowance.isHourly) {
    if (allowance.neverExpires) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.several.never_expire',
        { ...units, month: expiringMonth(allowance, cycleLimits) }
      )
    }
    if (allowance.expireInMonths < 12) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.several.expire_in_less_than_one_year',
        { ...units, month: expiringMonth(allowance, cycleLimits) }
      )
    }
    if (allowance.expireInYears === 1) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.several.expire_in_one_year',
        { ...units }
      )
    }
    if (allowance.expiresInRoundYears) {
      return i18n.t(
        'time_off.carry_over_explainer.carry_over.hours.description.several.expire_in_years',
        { ...units, years: allowance.expireInYears }
      )
    }
    return i18n.t(
      'time_off.carry_over_explainer.carry_over.hours.description.several.expire_in_months',
      { ...units, months: allowance.expireInMonths }
    )
  }

  throw new Error(`Missing translation for severalUnitsCarryOverAllowanceExplainer`)
}

function unlimitedCarryOverEmployeeCycleExplainer(
  allowance: Allowance,
  cycleLimits: Cycle | null | undefined
): string {
  if (allowance.isDays) {
    return allowance.neverExpires
      ? i18n.t('time_off.carry_over_explainer.carry_over.days.description.unlimited.never_expire')
      : i18n.t('time_off.carry_over_explainer.carry_over.days.description.unlimited.expire_on', {
          date: expiringDate(allowance, cycleLimits),
        })
  }

  if (allowance.isHourly) {
    return allowance.neverExpires
      ? i18n.t('time_off.carry_over_explainer.carry_over.hours.description.unlimited.never_expire')
      : i18n.t('time_off.carry_over_explainer.carry_over.hours.description.unlimited.expire_on', {
          date: expiringDate(allowance, cycleLimits),
        })
  }

  throw new Error(`Missing translation for unlimitedCarryOverEmployeeCycleExplainer`)
}

function oneUnitsCarryOverEmployeeCycleExplainer(
  allowance: Allowance,
  cycleLimits: Cycle | null | undefined
): string {
  if (allowance.isDays) {
    return allowance.neverExpires
      ? i18n.t('time_off.carry_over_explainer.carry_over.days.description.one.never_expire')
      : i18n.t('time_off.carry_over_explainer.carry_over.days.description.one.expire_on', {
          date: expiringDate(allowance, cycleLimits),
        })
  }
  if (allowance.isHourly) {
    return allowance.neverExpires
      ? i18n.t('time_off.carry_over_explainer.carry_over.hours.description.one.never_expire')
      : i18n.t('time_off.carry_over_explainer.carry_over.hours.description.one.expire_on', {
          date: expiringDate(allowance, cycleLimits),
        })
  }

  throw new Error(`Missing translation for oneUnitsCarryOverEmployeeCycleExplainer`)
}

function severalUnitsCarryOverEmployeeCycleExplainer(
  allowance: Allowance,
  cycleLimits: Cycle | null | undefined
): string {
  const units = { units: allowance.carryOverUnits }

  if (allowance.isDays) {
    return allowance.neverExpires
      ? i18n.t('time_off.carry_over_explainer.carry_over.days.description.several.never_expire', {
          ...units,
        })
      : i18n.t('time_off.carry_over_explainer.carry_over.days.description.several.expire_on', {
          ...units,
          date: expiringDate(allowance, cycleLimits),
        })
  }
  if (allowance.isHourly) {
    return allowance.neverExpires
      ? i18n.t('time_off.carry_over_explainer.carry_over.hours.description.several.never_expire', {
          ...units,
        })
      : i18n.t('time_off.carry_over_explainer.carry_over.hours.description.several.expire_on', {
          ...units,
          date: expiringDate(allowance, cycleLimits),
        })
  }

  throw new Error(`Missing translation for severalUnitsCarryOverEmployeeCycleExplainer`)
}

function expiringDate(allowance: Allowance, cycleLimits: Cycle | null | undefined): string {
  if (!cycleLimits) {
    return ''
  }

  const expiringDate = cycleLimits.end.clone().add(allowance.expireInMonths, 'months')

  return expiringDate.format('DD MMM YYYY')
}

function expiringMonth(allowance: Allowance, cycleLimits: Cycle | null | undefined): string {
  if (!cycleLimits) {
    return ''
  }

  return cycleLimits.end.clone().add(allowance.expireInMonths, 'months').format('MMMM')
}

export default function carryOverUnitsDescription(
  allowance: Allowance,
  cycleLimits: Cycle | null = null
): string {
  const carryOverStrategy = allowance.carryOverStrategy
  const employeeCircleExplainer = EMPLOYEE_CYCLE_EXPLAINERS[carryOverStrategy]
  const allowanceCycleExplainer = ALLOWANCE_CYCLE_EXPLAINERS[carryOverStrategy]

  if (allowance.isEmployeeCycle && employeeCircleExplainer !== undefined) {
    return employeeCircleExplainer(allowance, cycleLimits)
  }

  if (allowanceCycleExplainer !== undefined) {
    return allowanceCycleExplainer(allowance, cycleLimits)
  }

  return ''
}
